/**
 * TokenBalanceContext - Global Q.AI Token Balance State
 *
 * Provides centralized token balance management across all Q.AI components.
 * Updates in one place are reflected everywhere.
 *
 * Features:
 * - Single source of truth for token balance
 * - Automatic refresh on mount (with JWT token)
 * - Manual refresh capability
 * - Low balance detection
 * - Loading states
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getBalance } from '../api/qaiApi';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const LOW_BALANCE_THRESHOLD = 15;

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════════════════

const TokenBalanceContext = createContext(null);

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════════════

export const TokenBalanceProvider = ({ children }) => {
    // State
    const [balance, setBalance] = useState(null); // null = loading, number = actual balance
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Track if initial fetch has been done
    const initialFetchDone = useRef(false);

    /**
     * Get Q.AI JWT token from localStorage
     */
    const getQAIToken = useCallback(() => {
        return localStorage.getItem('qai_token');
    }, []);

    /**
     * Refresh balance from API
     * @returns {Promise<{balance: number}|null>}
     */
    const refreshBalance = useCallback(async () => {
        const token = getQAIToken();

        if (!token) {
            console.log('[TokenBalanceContext] No token, skipping balance fetch');
            setBalance(0);
            return null;
        }

        setIsLoading(true);
        setError(null);

        try {
            const data = await getBalance(token);

            if (data && typeof data.balance === 'number') {
                setBalance(data.balance);
                setLastUpdated(new Date());
                console.log('[TokenBalanceContext] Balance updated:', data.balance);
                return { balance: data.balance };
            } else {
                console.warn('[TokenBalanceContext] Invalid balance response:', data);
                setError('Invalid balance data');
                return null;
            }
        } catch (err) {
            console.error('[TokenBalanceContext] Failed to fetch balance:', err);
            setError(err.message || 'Failed to fetch balance');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [getQAIToken]);

    /**
     * Set balance directly (used after successful payment)
     * @param {number} newBalance
     */
    const updateBalance = useCallback((newBalance) => {
        if (typeof newBalance === 'number') {
            setBalance(newBalance);
            setLastUpdated(new Date());
            console.log('[TokenBalanceContext] Balance set directly:', newBalance);
        }
    }, []);

    /**
     * Deduct tokens (optimistic update)
     * @param {number} amount - Amount to deduct
     */
    const deductTokens = useCallback((amount) => {
        setBalance(prev => {
            const newBalance = Math.max(0, (prev || 0) - amount);
            console.log('[TokenBalanceContext] Tokens deducted:', amount, '-> new balance:', newBalance);
            return newBalance;
        });
        setLastUpdated(new Date());
    }, []);

    /**
     * Add tokens (after purchase)
     * @param {number} amount - Amount to add
     */
    const addTokens = useCallback((amount) => {
        setBalance(prev => {
            const newBalance = (prev || 0) + amount;
            console.log('[TokenBalanceContext] Tokens added:', amount, '-> new balance:', newBalance);
            return newBalance;
        });
        setLastUpdated(new Date());
    }, []);

    // Computed values
    const isLowBalance = balance !== null && balance <= LOW_BALANCE_THRESHOLD;
    const hasTokens = balance !== null && balance > 0;

    // Initial fetch when token becomes available
    useEffect(() => {
        const token = getQAIToken();

        if (token && !initialFetchDone.current) {
            console.log('[TokenBalanceContext] Token detected, fetching initial balance...');
            initialFetchDone.current = true;
            refreshBalance();
        }
    }, [getQAIToken, refreshBalance]);

    // Context value
    const value = {
        // State
        balance,
        isLoading,
        error,
        lastUpdated,
        isLowBalance,
        hasTokens,

        // Actions
        refreshBalance,
        updateBalance,
        deductTokens,
        addTokens,

        // Constants
        LOW_BALANCE_THRESHOLD
    };

    return (
        <TokenBalanceContext.Provider value={value}>
            {children}
        </TokenBalanceContext.Provider>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * useTokenBalance hook
 * @returns {Object} Token balance context value
 */
export const useTokenBalance = () => {
    const context = useContext(TokenBalanceContext);

    if (!context) {
        throw new Error('useTokenBalance must be used within a TokenBalanceProvider');
    }

    return context;
};

export default TokenBalanceContext;
